import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetTermsOfMonitoring, useMonitoringStore } from 'services/monitoring';
import { termOption, termsOptions } from './utils';

const terms: { [key: string]: string } = {
	ka: `${process.env.REACT_APP_CDN_URL}assets/static/legal/monitoring/specific-terms/farmer-ka.pdf`,
	en: `${process.env.REACT_APP_CDN_URL}assets/static/legal/monitoring/specific-terms/farmer-en.pdf`,
};

const TermItem = ({ name, id, termsData }: termOption) => {
	const value = termsData?.[id];
	const { t } = useTranslation();

	const formattedValue = () => {
		switch (id) {
			case 'price':
				return `€${value}`;
			case 'duration':
				return `${value} ${t('year')}`;
			case 'phases':
				return `${value} ${t('phasesStep')}`;
			default:
				return value;
		}
	};

	return (
		<div className="flex justify-center flex-col gap-2 border-light border-b pb-5">
			<span className="text-dark font-bold text-base font-roboto">
				{t(`findSpecialistPage.monitoring.${name}`)}
			</span>
			<span
				className={classNames('font-roboto', {
					'font-bold text-primary': id === 'price',
					'font-base text-secondary': id !== 'price',
				})}
			>
				{formattedValue()}
			</span>
		</div>
	);
};

export const TermsStep = () => {
	const { t, i18n } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { initialStepData } = useMonitoringStore();
	const [isNavigating, setIsNavigating] = useState(false);

	const { data: termsOfMonitoring, isLoading } = useGetTermsOfMonitoring();
	
	// Clear saved form data
	const clearSavedFormData = () => {
		try {
			localStorage.removeItem('monitoringPackagesFormData');
		} catch (error) {
			console.error('Error clearing form data from localStorage:', error);
		}
	};

	useEffect(() => {
		if (!initialStepData.id) {
			navigateWithLanguage('/farmer/services/monitoring/packages');
		}
	}, [initialStepData.id, navigateWithLanguage]);

	const handleNextStep = () => {
		if (isNavigating) return;
		setIsNavigating(true);
		
		// Don't clear form data here - it will be cleared when payment is actually processed
		
		setTimeout(() => {
			navigateWithLanguage('/farmer/services/monitoring/payment');
			setIsNavigating(false);
		}, 100);
	};
	
	const handlePreviousClick = (e: React.MouseEvent) => {
		e.preventDefault();
		if (isNavigating) return;
		setIsNavigating(true);
		
		// Navigate back to packages page without losing form data
		// Do NOT clear form data here - we want to preserve it when going back
		setTimeout(() => {
			// We're using React Router navigation which will preserve the Zustand store state
			navigateWithLanguage('/farmer/services/monitoring/packages');
			setIsNavigating(false);
		}, 100);
	};

	return (
		<div className="flex flex-col xs:mt-0 h-full gap-[20px] p-[20px] relative justify-between">
			<div className="flex items-center h-full justify-center sm:mt-0 mt-[20px]">
				<div className="flex flex-wrap flex-col max-w-[500px] w-full gap-[20px]">
					{isLoading ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100px',
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<>
							<h1 className="text-dark text-center font-bold text-xl leading-normal mb-[20px] mt-[50px]">
								{t('findSpecialistPage.monitoring.termsOfMonitoring')}
							</h1>
							{termsOptions.map((option, index) => (
								<div key={index}>
									{isLoading ? (
										<div>Loading...</div>
									) : (
										<TermItem {...option} termsData={termsOfMonitoring} />
									)}
								</div>
							))}
							<div className="flex flex-col sm:flex-row items-center gap-1">
								<p className="text-secondary font-roboto text-base font-normal leading-normal">
									{t('byPlacingOrder')}
								</p>
								<a
									className="font-roboto text-primary flex items-end justify-end text-base font-bold underline cursor-pointer"
									href={terms[i18n.language]}
									target="_blank"
									rel="noreferrer"
								>
									{t('monitoringSpecificTerms')}
								</a>
							</div>

							<div className="p-[10px] bg-[#FFF2DF] border-custom-yellow border-2 rounded-lg">
								<span className="text-base text-dark font-normal font-roboto">
									{t('findSpecialistPage.monitoring.gainAccess')}
								</span>
							</div>
							<Button 
								className="w-full" 
								type="button" 
								variant="contained" 
								onClick={handleNextStep}
								disabled={isNavigating}
							>
								{isNavigating ? (
									<CircularProgress size={24} color="inherit" />
								) : (
									t('findSpecialistPage.monitoring.placeOrder')
								)}
							</Button>
						</>
					)}
				</div>
			</div>

			<Button
				className="w-[120px]"
				type="button"
				onClick={handlePreviousClick}
				sx={{ backgroundColor: 'white', color: 'black' }}
				variant="contained"
				startIcon={<Icon path={mdiChevronLeft} size={1} />}
				disabled={isNavigating}
			>
				{isNavigating ? (
					<CircularProgress size={24} color="inherit" />
				) : (
					t('previous')
				)}
			</Button>
		</div>
	);
};
